import { List } from "immutable";
import moment from "moment-timezone";
import { createSelector } from "reselect";
import { isAllDay } from "~/components/Calendar/calendarUtils.js";
import { getIsDateWorkday } from "~/utils/workday";
import {
  getFirstWeekDay,
  getRawConfigOption,
  getRawPersonalizedConfigOption,
  getTimeWorkdays,
  getUserTimezone,
} from "./selector.configOptions.jsx";
import { getGanttDaysOff } from "./selector.daysOff.jsx";
import { getAllProjectsSortedByKey } from "./selector.projects.jsx";
import { getActiveUsersMapSortedBy } from "./selector.users.jsx";
import { getAllWeeklyUserCapacities } from "./selector.weeklyWorkerCapacity.js";

export const getIfTaskDatesOverlap = (firstTask, secondTask) => {
  const firstTaskStartOn = firstTask.start_on;
  const firstTaskDueOn = firstTask.due_on;
  const secondTaskStartOn = secondTask.start_on;
  const secondTaskDueOn = secondTask.due_on;

  return firstTaskStartOn <= secondTaskDueOn && secondTaskStartOn <= firstTaskDueOn;
};

export const getNextWorkingDay = createSelector(
  getTimeWorkdays,
  getGanttDaysOff,
  getUserTimezone,
  (workDays, daysOff, timezone) => {
    const currentDateInTimezone = moment().tz(timezone).format("YYYY-MM-DD");
    const today = moment.utc(currentDateInTimezone);
    let date = today.clone().add(1, `days`).startOf("day");
    while (!getIsDateWorkday(workDays, daysOff, date)) {
      date = date.add(1, "days");
    }

    return date.unix();
  }
);

export const getFilteredWorkloadUserIds = createSelector(
  (state) => getRawPersonalizedConfigOption(state, `workload_people_picker`),
  (data = []) => List(data)
);

export const getWorkloadUsers = createSelector(getActiveUsersMapSortedBy, (users) =>
  users.filter((user) => user.hasElevatedPermissions())
);

const getFilteredUserIds = createSelector(getFilteredWorkloadUserIds, (filteredUserIds) => filteredUserIds.toArray());

const filteredWorkloadActiveUsers = createSelector(getWorkloadUsers, getFilteredUserIds, (users, filteredIds) => {
  if (filteredIds && filteredIds.length) {
    return users.toArray().filter((user) => filteredIds.indexOf(user.id) !== -1);
  }
  return users;
});

export const getRows = createSelector(
  filteredWorkloadActiveUsers,
  (state) => getRawConfigOption(state, `weekly_worker_capacity`),
  getAllWeeklyUserCapacities,
  getFirstWeekDay,
  (users, defaultWeeklyCapacity, allWeeklyCapacity, firstWeekDay) => {
    let rows = [
      {
        userId: 0,
        userWeeklyCapacity: [0, 0, 0, 0, 0, 0, 0],
      },
    ];

    const defaultCapacity = defaultWeeklyCapacity ? defaultWeeklyCapacity.toArray() : [0, 480, 480, 480, 480, 480, 0];
    const hasWeeklyCapacity = Object.keys(allWeeklyCapacity).length !== 0;
    users.forEach((user) => {
      const capacity = hasWeeklyCapacity && allWeeklyCapacity[user.id] ? allWeeklyCapacity[user.id] : defaultCapacity;

      const reverseCapacity = [...capacity];
      const sunday = reverseCapacity.shift();
      reverseCapacity.push(sunday);

      rows.push({
        userId: user.id,
        userWeeklyCapacity: firstWeekDay ? reverseCapacity : capacity,
      });
    });

    return rows;
  }
);

export const getWorkloadProjectIds = createSelector(getAllProjectsSortedByKey, (projects) =>
  projects.map((project) => project.id).toArray()
);

const isMultiDay = (event) => {
  if (event.start_on && event.ends_on) {
    if (!moment.unix(event.starts_on).isSame(moment.unix(event.ends_on), "date")) {
      return true;
    }
  } else {
    if (!moment(event.start).isSame(moment(event.end), "date")) {
      return true;
    }
  }
  return false;
};

export const sortEvents = (a, b) => {
  if (isAllDay(a) && isAllDay(b)) {
    if (a.name && b.name) {
      return a.name.localeCompare(b.name);
    }
    return 0;
  } else if (isAllDay(a) && !isAllDay(b)) {
    return -1;
  } else if (!isAllDay(a) && isAllDay(b)) {
    return 1;
  } else if (isMultiDay(a) && isMultiDay(b)) {
    if (a.name && b.name) {
      return a.name.localeCompare(b.name);
    }
    if (a.starts_on && b.starts_on) {
      return a.starts_on < b.starts_on ? -1 : 1;
    }
    if (a.start && b.start) {
      return a.start < b.start ? -1 : 1;
    }
  } else if (isMultiDay(a) && !isMultiDay(b)) {
    return -1;
  } else if (!isMultiDay(a) && isMultiDay(b)) {
    return 1;
  } else {
    if (a.starts_on && b.starts_on) {
      return a.starts_on < b.starts_on ? -1 : 1;
    }
    if (a.start && b.start) {
      return a.start < b.start ? -1 : 1;
    }
  }
};

export const getSortedWorkloadUsersProjectsMap = createSelector(
  filteredWorkloadActiveUsers,
  (state, sortBy = `name`) => getAllProjectsSortedByKey(state, sortBy),
  (users, projects) => {
    const result = projects
      .filter((project) => {
        return !project.get(`is_trashed`) && !project.get(`is_sample`);
      })
      .map((project) => ({
        id: project.id,
        members: project.members.toArray(),
      }))
      .toArray();

    let usersProjectsMap = {};

    let userProjectsIds = result.map((project) => project.id);
    usersProjectsMap[0] = [0, ...userProjectsIds, null];

    users.forEach((user) => {
      userProjectsIds = result.filter((project) => project.members.includes(user.id))?.map((project) => project.id);
      usersProjectsMap[user.id] = [0, ...userProjectsIds, null];
    });
    return usersProjectsMap;
  }
);

export const getColumns = createSelector(
  getFirstWeekDay,
  getTimeWorkdays,
  getGanttDaysOff,
  (state, { scaleStart }) => scaleStart,
  (state, { scaleEnd }) => scaleEnd,
  (state, { today }) => today,
  (firstDayOfWeek, workDays, dayOffs, scaleStart, scaleEnd, today) => {
    let columns = [];
    today = moment.utc(today).startOf("day");

    const currentDate = moment.unix(scaleStart).utc();
    const lastDate = moment.unix(scaleEnd).utc();

    let id = 0;

    while (currentDate.isSameOrBefore(lastDate)) {
      const currentTimestamp = currentDate.unix();
      columns.push({
        id,
        dateMeta: {
          timestamp: currentTimestamp,
          day: currentDate.date(),
          // because moment counts months form 0 - 11
          month: currentDate.month() + 1,
          year: currentDate.year(),
          weekday: currentDate.weekday(),
          monthName: currentDate.format(`MMMM`),
          dayName: currentDate.format(`ddd`),
        },
        isWorkday: getIsDateWorkday(workDays, dayOffs, currentDate),
        isToday: currentDate.isSame(today),
      });
      currentDate.add(1, "day");
      id++;
    }

    return columns;
  }
);

export const getWorkloadDateRange = createSelector(getColumns, (columns) => {
  const firstMetaDate = columns[0].dateMeta;
  const lastMetaDate = columns[columns.length - 1].dateMeta;
  return {
    start_date: `${firstMetaDate.year}-${firstMetaDate.month}-${firstMetaDate.day}`,
    end_date: `${lastMetaDate.year}-${lastMetaDate.month}-${lastMetaDate.day}`,
  };
});

export const makeGetWorkloadProjectIdsByUserId = () => {
  return createSelector(
    (state, userId, sortBy = `name`) => getAllProjectsSortedByKey(state, sortBy),
    (state, userId) => userId,
    (projects, userId) => {
      if (userId === 0) {
        return projects.map((project) => project.id).toArray();
      }
      return projects
        .filter((project) => {
          return !project.get(`is_trashed`) && !project.get(`is_sample`) && project.members.indexOf(userId) > -1;
        })
        .map((project) => project.id)
        .toArray();
    }
  );
};

export const getShowCompletedTasksByUserId = createSelector(
  (state) => state.workload,
  (state, userId) => userId,
  (toggleUsers, userId) => {
    if (toggleUsers && toggleUsers[userId]) {
      return toggleUsers[userId];
    } else {
      return false;
    }
  }
);
